/**
 * Cloud Token Service
 * 
 * Handles token storage, refresh, and management for cloud providers
 */

import googleTokenRefreshService from '../googleTokenRefreshService'

class CloudTokenService {
  /**
   * Refresh Google Drive access token with cascading auth token refresh
   */
  async refreshAccessToken() {
    // CRITICAL: Check if user is authenticated before refreshing token
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudTokenService: User not authenticated, blocking token refresh')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudTokenService: Onboarding detected, skipping token refresh')
      throw new Error('Token refresh not available during onboarding')
    }
    
    // Get refresh token from localStorage
    const refreshToken = localStorage.getItem('cloudRefreshToken')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    
    try {
      console.log('🔄 CloudTokenService: Refreshing Google Drive access token...')
      
      // First, ensure auth token is valid (cascading refresh)
      const authTokenExpiry = localStorage.getItem('authTokenExpiry')
      if (this.isTokenExpired(authTokenExpiry)) {
        console.log('🔄 CloudTokenService: Auth token expired, refreshing first...')
        const authRefreshed = await this.refreshAuthToken()
        if (!authRefreshed) {
          throw new Error('Failed to refresh auth token')
        }
      }
      
      // Call backend endpoint to refresh token using the stored client secret
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      })
      
      if (!response.ok) {
        // If we get 401, the auth token might still be expired - try refreshing it once more
        if (response.status === 401) {
          console.log('🔄 CloudTokenService: Got 401, attempting auth token refresh...')
          const authRefreshed = await this.refreshAuthToken()
          if (authRefreshed) {
            // Retry the cloud token refresh with the new auth token
            return await this.refreshAccessToken()
          }
        }
        
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Failed to refresh token: ${response.status} - ${errorData.error || 'Unknown error'}`)
      }
      
      const refreshData = await response.json()
      
      if (refreshData.success && refreshData.accessToken) {
        console.log('✅ CloudTokenService: Google Drive access token refreshed successfully')
        
        // Store tokens using the enhanced method
        this.storeTokens(refreshData.accessToken, refreshData.refreshToken, refreshData.userInfo)
        
        // Update GoogleDriveAPI with new access token
        this.updateGoogleDriveAPI(refreshData.accessToken)
        
        // Dispatch events for other services
        this.dispatchTokenReadyEvent(refreshData.accessToken, refreshData.userInfo)
        
        return refreshData.accessToken
      }
      
      throw new Error('Failed to refresh access token')
      
    } catch (error) {
      console.error('❌ CloudTokenService: Failed to refresh Google Drive access token:', error)
      throw error
    }
  }

  /**
   * Refresh backend auth token
   */
  async refreshAuthToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        console.log('❌ CloudTokenService: No backend refresh token available')
        return false
      }
      
      console.log('🔄 CloudTokenService: Refreshing backend auth token...')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      })
      
      if (!response.ok) {
        console.error('❌ CloudTokenService: Auth token refresh failed:', response.status)
        return false
      }
      
      const data = await response.json()
      
      if (data.authToken) {
        localStorage.setItem('authToken', data.authToken)
        if (data.expiryTime) {
          localStorage.setItem('authTokenExpiry', data.expiryTime)
        }
        console.log('✅ CloudTokenService: Backend auth token refreshed successfully')
        return true
      }
      
      return false
      
    } catch (error) {
      console.error('❌ CloudTokenService: Auth token refresh failed:', error)
      return false
    }
  }

  /**
   * Check if a token is expired
   */
  isTokenExpired(expiryString) {
    if (!expiryString) return true
    
    try {
      const expiryTime = new Date(expiryString)
      const now = new Date()
      const bufferTime = 5 * 60 * 1000 // 5 minutes buffer
      
      return (expiryTime.getTime() - now.getTime()) < bufferTime
    } catch (error) {
      console.error('❌ CloudTokenService: Error checking token expiry:', error)
      return true
    }
  }

  /**
   * Store tokens in localStorage
   */
  storeTokens(accessToken, refreshToken, userInfo) {
    if (accessToken) {
      localStorage.setItem('cloudAccessToken', accessToken)
      
          // CRITICAL: Also set the expiry in localStorage for proactive refresh
    const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
    localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
    }
    if (refreshToken) {
      localStorage.setItem('cloudRefreshToken', refreshToken)
    }
    if (userInfo) {
      localStorage.setItem('cloudUserInfo', JSON.stringify(userInfo))
    }
    localStorage.setItem('cloudProvider', 'google_drive')
  }

  /**
   * Get stored tokens from localStorage
   */
  getStoredTokens() {
    return {
      accessToken: localStorage.getItem('cloudAccessToken'),
      refreshToken: localStorage.getItem('cloudRefreshToken'),
      userInfo: localStorage.getItem('cloudUserInfo'),
      provider: localStorage.getItem('cloudProvider')
    }
  }

  /**
   * Clear all cloud tokens
   */
  clearAllTokens() {
    localStorage.removeItem('cloudAccessToken')
    localStorage.removeItem('cloudRefreshToken')
    localStorage.removeItem('cloudUserInfo')
    localStorage.removeItem('cloudProvider')
    localStorage.removeItem('googleDriveTokenExpiry')
  }

  /**
   * Clear access token only (preserve refresh token)
   */
  clearAccessToken() {
    localStorage.removeItem('cloudAccessToken')
  }

  /**
   * Check if tokens exist
   */
  hasTokens() {
    const { accessToken, refreshToken } = this.getStoredTokens()
    return !!(accessToken && refreshToken)
  }

  /**
   * Check if access token exists
   */
  hasAccessToken() {
    return !!localStorage.getItem('cloudAccessToken')
  }

  /**
   * Check if refresh token exists
   */
  hasRefreshToken() {
    return !!localStorage.getItem('cloudRefreshToken')
  }

  /**
   * Update GoogleDriveAPI with new access token
   */
  updateGoogleDriveAPI(accessToken) {
    if (window.googleDriveAPI && accessToken) {
      window.googleDriveAPI.updateAccessToken(accessToken)
      console.log('🔄 CloudTokenService: Updated GoogleDriveAPI with new access token')
    }
  }

  /**
   * Dispatch token ready event
   */
  dispatchTokenReadyEvent(accessToken, userInfo) {
    window.dispatchEvent(new CustomEvent('accessTokenReady', { 
      detail: { accessToken, userInfo }
    }))
    console.log('🔑 CloudTokenService: Dispatched accessTokenReady event')
  }

  /**
   * Dispatch Google Drive connected event
   */
  dispatchGoogleDriveConnectedEvent(accessToken, refreshToken, userInfo) {
    window.dispatchEvent(new CustomEvent('googleDriveConnected', {
      detail: { 
        accessToken, 
        refreshToken,
        userInfo 
      }
    }))
    console.log('✅ CloudTokenService: Dispatched googleDriveConnected event')
  }
}

export default new CloudTokenService()
